import React, { useCallback, useState, useEffect } from 'react'
import { useRouter } from 'next/router'
import { useDispatch, useSelector } from 'react-redux'
import { Grid, Loader, Panel, Tabs } from 'rsuite'
import { ContentContainer } from '@/components/styles'
import { configSelectors } from '@/store/configs'
import { searchActions, searchSelectors } from '@/store/search'
import useTranslation from '@/hooks/useTranslation'
import VideoModal from '@/components/videoModal'
import useVideoModal from '@/hooks/useVideoModal'
import { loggedInUserSelector } from '@/store/loggedInUser'
import { getPublications } from '@/utils/publications/helpers'
import { customerHelper, generalConstants } from '@tweddle-se-team/common/constants'
import VisualSearch from './visualSearch'
import styles from '../../../publications/publications.module.scss'
import localStyles from './publication.module.scss'
import { MAPPING_TAB_INDEX_NAMES } from '@/constants/digitalFirstConstants'
import Manuals from './manuals'
import VideoCard from './VideoCard'
import { customerId } from '@/utils/env'
import PreviewModeBanner from '@/components/shared/previewModeBanner'
import { overviewActions } from '@/store/overview'
import { getOverviewQueryParams } from '@/utils/overview/helpers'
import ExternalRedirect from './externalRedirect'

const { DFOM, OM, EOM, CSOM, MM, WTY, UG } = generalConstants
const { CUSTOMER_IDS } = customerHelper
const SUBARU_TAB_NAMES = MAPPING_TAB_INDEX_NAMES[customerId] || {}

// Default values for visual search navigation
const DEFAULT_VIEW = 'Front'
const DEFAULT_TOPIC_TYPE = 'all'

const update = async (filters, pagination, updatePagination, setSearchResults) => {
  updatePagination(pagination)
  const publications = await getPublications(filters, pagination)
  setSearchResults(publications)
}
const DEFAULT_ORDER = [DFOM, OM, EOM, CSOM, MM, WTY, UG]

const manualPublicationFilterMapper = {
  [CUSTOMER_IDS.SUBARU]: publication => publication.publicationType !== 'VIDEO',
  [CUSTOMER_IDS.NORDICS]: publication => publication.publicationType !== 'VIDEO' && publication.publicationType !== DFOM
}

const manualPublicationFilter =
  manualPublicationFilterMapper[customerId] || manualPublicationFilterMapper[CUSTOMER_IDS.SUBARU]

const noPublicationsMapper = {
  [CUSTOMER_IDS.SUBARU]: ({ t, url }) => (
    <ExternalRedirect externalUrl={url} buttonText={t('no_pubs_found', 'No publication found. Try this link')} />
  ),
  [CUSTOMER_IDS.NORDICS]: ({ t }) => (
    <h2 className="text-center mt-20">{t('no_publication_found', 'No publication found')}</h2>
  )
}

const NoPublications = ({ vehicleData }) => {
  const t = useTranslation()
  const url = vehicleData?.resourceUrl || 'https://www.subaru.com/owners'
  const renderNoPublications = noPublicationsMapper[customerId] || noPublicationsMapper[CUSTOMER_IDS.SUBARU]

  return renderNoPublications({ t, url })
}

const Publications = ({ hotspots, baseDFOMEngPublication, digitalFirstOwnerManual, vehicleData, isPreviewMode }) => {
  // <Redux>
  const dispatch = useDispatch()
  const router = useRouter()
  const { query } = router
  const { isInFrame } = useSelector(configSelectors.selectConfigs)
  const publicationTypes = useSelector(configSelectors.selectConfigByKey('publicationTypes'))
  const { filterBy, pagination } = useSelector(searchSelectors.selectQuery)
  const { publications } = useSelector(searchSelectors.selectResults)
  const user = useSelector(loggedInUserSelector.selectUser)
  // </Redux>
  const [loading, setLoading] = useState(false)
  const { videoTitle, videoUrl, openVideoModal, closeVideoModal, isVideoModalOpen } = useVideoModal()
  const t = useTranslation()
  const { items = [], total } = publications
  const { page, limit } = pagination
  const orderMap = Object.values(publicationTypes || {}).reduce((m, x) => {
    if (x?.order) {
      const key = x?.value || x?.name
      if (key) m[key] = parseInt(x.order)
    }
    return m
  }, {})
  const BUCKET = { CONFIG: 0, DEFAULT: 1000, UNKNOWN: 2000 }
  const defaultIndex = DEFAULT_ORDER.reduce((m, t, i) => {
    m[t] = i
    return m
  }, {})

  const score = t =>
    t in orderMap ? BUCKET.CONFIG + orderMap[t] : t in defaultIndex ? BUCKET.DEFAULT + defaultIndex[t] : BUCKET.UNKNOWN

  const videoContent = items
    .filter(i => i.publicationType === 'VIDEO')
    .map(({ contents }) => contents)
    .flat()

  let data = items
    .filter(manualPublicationFilter)
    .map(({ _id, partNumber, publicationType, language, brand, model, modelType, contents, year }) => ({
      _id,
      partNumber,
      language,
      brand,
      model,
      modelType,
      year,
      publicationType,
      contents: {
        ditaId: contents.ditaId,
        webview: contents.webview,
        highres: contents.highres
      }
    }))
    .sort(
      (a, b) =>
        score(a.publicationType) - score(b.publicationType) || a.publicationType.localeCompare(b.publicationType)
    )

  const setSearchResults = useCallback(
    publications => dispatch(searchActions.SET_PUBLICATIONS_RESULT(publications)),
    [dispatch]
  )
  const updatePagination = useCallback(pagination => dispatch(searchActions.UPDATE_PAGINATION(pagination)), [dispatch])
  const updateOverview = useCallback(payload => dispatch(overviewActions.UPDATE_OPTIONS(payload)), [dispatch])

  const changePage = useCallback(
    async (page = 1) => {
      setLoading(true)
      try {
        const newPagination = { ...pagination, page }
        await update(filterBy, newPagination, updatePagination, setSearchResults)
      } finally {
        setLoading(false)
      }
    },
    [filterBy, pagination, updatePagination, setSearchResults]
  )

  const updateQueryParams = useCallback(
    params => {
      const updatedQuery = { ...query, ...params }
      updateOverview(getOverviewQueryParams(updatedQuery))
      router.replace(
        {
          pathname: router.pathname,
          query: updatedQuery
        },
        undefined,
        { shallow: true }
      )
    },
    [router, query, updateOverview]
  )

  const onTabChange = useCallback(
    value => {
      updateQueryParams({ selectedTab: value })
    },
    [updateQueryParams]
  )

  // Check if hotspots have data in any view
  const hasHotspots = React.useMemo(() => {
    // In preview mode, don't require isPublished
    if (!isPreviewMode && !hotspots?.isPublished) return false
    if (!hotspots?.mapping) return false
    // Check if any view has spots
    return Object.values(hotspots.mapping).some(view => view?.spots && view.spots.length > 0)
  }, [hotspots, isPreviewMode])

  const tabVisibility = {
    [SUBARU_TAB_NAMES.VISUAL_SEARCH]: hasHotspots,
    [SUBARU_TAB_NAMES.MANUALS]: true,
    [SUBARU_TAB_NAMES.VIDEOS]: videoContent?.length > 0
  }

  // Find the first visible tab as the default (priority order: VISUAL_SEARCH -> MANUALS -> VIDEOS)
  const tabOrder = [SUBARU_TAB_NAMES.VISUAL_SEARCH, SUBARU_TAB_NAMES.MANUALS, SUBARU_TAB_NAMES.VIDEOS]
  const defaultTab = tabOrder.find(tab => tabVisibility[tab]) || SUBARU_TAB_NAMES.MANUALS

  // Use query.selectedTab if it's visible, otherwise use defaultTab
  const selectedTab = query.selectedTab && tabVisibility[query.selectedTab] ? query.selectedTab : defaultTab

  // Initialize default navigation values in URL
  useEffect(() => {
    if (router.isReady) {
      const initialParams = {}
      if (!query.selectedTab || !tabVisibility[query.selectedTab]) {
        initialParams.selectedTab = defaultTab
      }
      if (!query.selectedView) {
        initialParams.selectedView = DEFAULT_VIEW
      }
      if (!query.selectedTopicType) {
        initialParams.selectedTopicType = DEFAULT_TOPIC_TYPE
      }
      if (Object.keys(initialParams).length > 0) {
        updateQueryParams(initialParams)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady])

  return (
    <>
      {isPreviewMode && <PreviewModeBanner />}
      <div className={localStyles.centeredTabs}>
        <Tabs activeKey={selectedTab} onSelect={onTabChange} appearance="subtle">
          {tabVisibility[SUBARU_TAB_NAMES.VISUAL_SEARCH] && (
            <Tabs.Tab eventKey={SUBARU_TAB_NAMES.VISUAL_SEARCH} title={t('pubications_visual_search', 'Visual Search')}>
              <ContentContainer>
                <Grid fluid>
                  {selectedTab === SUBARU_TAB_NAMES.VISUAL_SEARCH && (
                    <VisualSearch
                      hotspots={hotspots}
                      baseDFOMEngPublication={baseDFOMEngPublication}
                      digitalFirstOwnerManual={digitalFirstOwnerManual}
                      publicationTypes={publicationTypes}
                      publications={items}
                      publicationRowClassName={localStyles.publicationRow}
                      publicationListClassName={localStyles.publicationList}
                      openVideoModal={openVideoModal}
                      updateQueryParams={updateQueryParams}
                      noPublications={<NoPublications vehicleData={vehicleData} />}
                    />
                  )}
                </Grid>
              </ContentContainer>
            </Tabs.Tab>
          )}
          {tabVisibility[SUBARU_TAB_NAMES.MANUALS] && (
            <Tabs.Tab eventKey={SUBARU_TAB_NAMES.MANUALS} title={t('pubications_manuals', 'Manuals')}>
              <div className={localStyles.tabContent}>
                <Manuals
                  data={data}
                  publicationTypes={publicationTypes}
                  user={user}
                  isInFrame={isInFrame}
                  total={total}
                  limit={limit}
                  page={page}
                  onPageChange={changePage}
                  publicationRowClassName={localStyles.publicationRow}
                  withPanel={false}
                  withPartNumber={false}
                  noPublications={<NoPublications vehicleData={vehicleData} />}
                />
              </div>
            </Tabs.Tab>
          )}
          {tabVisibility[SUBARU_TAB_NAMES.VIDEOS] && (
            <Tabs.Tab eventKey={SUBARU_TAB_NAMES.VIDEOS} title={t('pubications_videos', 'Videos')}>
              <div className={localStyles.tabContent}>
                <Panel collapsible defaultExpanded className={styles.publicationsPanel}>
                  <ContentContainer>
                    <div className={localStyles.videoGrid}>
                      {videoContent.map(content => (
                        <VideoCard key={content._id} content={content} onClick={openVideoModal} />
                      ))}
                    </div>
                  </ContentContainer>
                </Panel>
              </div>
            </Tabs.Tab>
          )}
        </Tabs>
      </div>
      {loading && <Loader backdrop center vertical size="lg" />}
      <VideoModal show={isVideoModalOpen} close={closeVideoModal} url={videoUrl} title={videoTitle} />
    </>
  )
}

export default Publications
